import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircle from '@mui/icons-material/AccountCircle';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import InfoIcon from '@mui/icons-material/Info';
import ContactsIcon from '@mui/icons-material/Contacts';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import DraftsIcon from '@mui/icons-material/Drafts';
import BoltIcon from '@mui/icons-material/Bolt';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import CampaignIcon from '@mui/icons-material/Campaign';
import ForumIcon from '@mui/icons-material/Forum';
import { connect } from "react-redux";
import { logout } from '../store/actions/authActions';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider, Menu, MenuItem, Tooltip } from '@mui/material';
import StarsIcon from '@mui/icons-material/Stars';
import LoginModal from './LoginModal';
import TickitLogo from './TickitLogo';
import { useThemeMode } from './theme/ThemeModeProvider';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { WhatsApp } from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FavoriteIcon from '@mui/icons-material/Favorite';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { usesPlatformChrome } from './platform/pageChrome';
import { getPlatformNav, getPlatformHomePath, getEventIdFromPath, isNavItemActive } from './platform/platformNavConfig';
import {
    platformAppBarSx,
    platformNavHeightPx,
    platformColors,
} from './tickitPlatformTheme';

const listItemTextProps = {
    primaryTypographyProps: {
        fontFamily: "'Plus Jakarta Sans', TickitTwo, Helvetica, Arial, sans-serif",
        fontWeight: 600,
        fontSize: "0.95rem",
    },
};

function MenuAppBar({ auth, logout }) {
    const navigate = useNavigate()
    const [sidebarOpen, setSidebarOpen] = React.useState(false);
    const [loginOpen, setLoginOpen] = React.useState(false);
    const [accountMenuAnchor, setAccountMenuAnchor] = React.useState(null);
    const accountMenuOpen = Boolean(accountMenuAnchor);

    const token = auth.token || '';
    const header = token.split(' ')
    const role = header[0]
    const location = useLocation();
    const platformMode = usesPlatformChrome(location.pathname, role);
    const { mode: themeMode, canToggle: canToggleTheme, toggleTheme } = useThemeMode();
    const marketingAppBarSx = {
        background: 'rgba(14, 5, 32, 0.9) !important',
        backdropFilter: 'blur(16px)',
        boxShadow: 'none !important',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08) !important',
        '&.MuiAppBar-root': {
            background: 'rgba(14, 5, 32, 0.9) !important',
        },
    };
    const appBarSx = platformMode ? platformAppBarSx : marketingAppBarSx;
    const toolbarHeight = `${platformNavHeightPx}px !important`;

    const userInitials = (auth?.user?.name || auth?.user?.username || role || 'U')
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const closeSidebar = () => setSidebarOpen(false);

    const openSidebar = () => setSidebarOpen(true);

    const handleLogout = () => {
        closeSidebar();
        closeAccountMenu();
        logout();
    };

    const handleHome = () => {
        closeSidebar();
        navigate("/");
    };

    const handlePlatformHome = () => {
        closeSidebar();
        navigate(getPlatformHomePath(role));
    };

    const handleProfile = () => {
        closeSidebar();
        setAccountMenuAnchor(null);
        const path = role === "Super" || role === "Admin"
            ? `/${role}`
            : `/${role}/profile`;
        navigate(path);
    };

    const openAccountMenu = (event) => {
        setAccountMenuAnchor(event.currentTarget);
    };

    const closeAccountMenu = () => {
        setAccountMenuAnchor(null);
    };

    const handleChangePassword = () => {
        closeAccountMenu();
        closeSidebar();
        if (role === "Super" || role === "Admin" || role === "Partner") {
            navigate(`/${role}/ChangePassword`);
        }
    };

    const route = (path) => {
        closeSidebar();
        navigate(path);
    }

    const navIconSx = { color: 'var(--primaryPurple)' };
    const platformNavConfig = platformMode ? getPlatformNav(role, location.pathname, auth?.user) : null;

    const renderDrawerFooter = () => (
        <>
            <Divider sx={{ my: 1.5 }} />
            {platformMode && canToggleTheme && (
                <ListItemButton onClick={toggleTheme} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 42 }}>
                        {themeMode === 'dark' ? (
                            <LightModeOutlinedIcon sx={navIconSx} />
                        ) : (
                            <DarkModeOutlinedIcon sx={navIconSx} />
                        )}
                    </ListItemIcon>
                    <ListItemText
                        primary={themeMode === 'dark' ? 'Light mode' : 'Dark mode'}
                        {...listItemTextProps}
                    />
                </ListItemButton>
            )}
            {role ? (
                <>
                    {(role === "Super" || role === "Admin" || role === "Partner") && (
                        <ListItemButton
                            onClick={() => route(`/${role}/ChangePassword`)}
                            sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}
                        >
                            <ListItemIcon sx={{ minWidth: 42 }}><EnhancedEncryptionIcon sx={navIconSx} /></ListItemIcon>
                            <ListItemText primary="Change password" {...listItemTextProps} />
                        </ListItemButton>
                    )}
                    <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><LogoutIcon sx={{ color: 'error.main' }} /></ListItemIcon>
                        <ListItemText primary="Logout" primaryTypographyProps={{ ...listItemTextProps.primaryTypographyProps, color: 'error.main' }} />
                    </ListItemButton>
                </>
            ) : (
                <ListItemButton onClick={() => route("/Login")} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 42 }}><LoginIcon sx={{ color: 'success.main' }} /></ListItemIcon>
                    <ListItemText primary="Login" primaryTypographyProps={{ ...listItemTextProps.primaryTypographyProps, color: 'success.main' }} />
                </ListItemButton>
            )}
        </>
    );

    const sidebarContent = (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box
                sx={{
                    background: 'linear-gradient(90deg, #1E0550 0%, #46109B 48%, #5A1EC8 100%)',
                    px: 2,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <TickitLogo
                    height={34}
                    variant="onDark"
                    onClick={platformNavConfig ? handlePlatformHome : handleHome}
                />
                <IconButton
                    size="small"
                    onClick={closeSidebar}
                    aria-label="Close menu"
                    sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <List sx={{ py: 1.5, flex: 1, overflow: 'auto' }} dense>
                {platformNavConfig ? (
                    <>
                        {platformNavConfig.parentNav && (
                            <ListItemButton
                                onClick={() => route(platformNavConfig.parentNav.path)}
                                sx={{
                                    borderRadius: 1,
                                    mx: 1,
                                    mb: 0.75,
                                    bgcolor: 'rgba(107, 45, 197, 0.08)',
                                    border: '1px solid rgba(107, 45, 197, 0.2)',
                                    '&:hover': { bgcolor: 'rgba(107, 45, 197, 0.14)' },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 42 }}>
                                    {platformNavConfig.parentNav.icon ? (
                                        <platformNavConfig.parentNav.icon sx={navIconSx} />
                                    ) : (
                                        <ArrowBackIcon sx={navIconSx} />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary={platformNavConfig.parentNav.label} {...listItemTextProps} />
                            </ListItemButton>
                        )}
                        {platformNavConfig.items.map((item) => {
                            const Icon = item.icon;
                            const active = isNavItemActive(location.pathname, item.path);
                            return (
                                <ListItemButton
                                    key={`${item.path}-${item.label}`}
                                    selected={active}
                                    onClick={() => route(item.path)}
                                    sx={{
                                        borderRadius: 1,
                                        mx: 1,
                                        mb: 0.25,
                                        '&.Mui-selected': {
                                            bgcolor: 'rgba(107, 45, 197, 0.12)',
                                            '&:hover': { bgcolor: 'rgba(107, 45, 197, 0.16)' },
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 42 }}><Icon sx={navIconSx} /></ListItemIcon>
                                    <ListItemText primary={item.label} {...listItemTextProps} />
                                </ListItemButton>
                            );
                        })}
                        {renderDrawerFooter()}
                    </>
                ) : role !== "Usher" && (<>
                    {role === "Partner" ? (
                        <ListItemButton onClick={() => route(`/Partner/events`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 42 }}><EventIcon sx={navIconSx} /></ListItemIcon>
                            <ListItemText primary="Search Events" {...listItemTextProps} />
                        </ListItemButton>
                    ) : (
                        <ListItemButton onClick={() => route(`/events`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 42 }}><EventIcon sx={navIconSx} /></ListItemIcon>
                            <ListItemText primary="Search Events" {...listItemTextProps} />
                        </ListItemButton>
                    )}
                    {(!role || role === "User" || role === "Partner") && (<>
                        <ListItemButton onClick={() => route(`/features`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 42 }}><ViewModuleIcon sx={navIconSx} /></ListItemIcon>
                            <ListItemText primary="Features" {...listItemTextProps} />
                        </ListItemButton>
                        <ListItemButton onClick={() => route(`/events/request`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 42 }}><FavoriteIcon sx={navIconSx} /></ListItemIcon>
                            <ListItemText primary="Weddings" {...listItemTextProps} />
                        </ListItemButton>
                    </>)}
                    {role === "Super" && (
                        <ListItemButton onClick={() => route(`/Super/Partner/search`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 42 }}><PersonSearchIcon sx={navIconSx} /></ListItemIcon>
                            <ListItemText primary="Search Partners" {...listItemTextProps} />
                        </ListItemButton>
                    )}
                </>)}
                {role === "User" && (<>
                    <ListItemButton onClick={() => route(`/${role}/tickets`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><LocalActivityIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="My Tickets" {...listItemTextProps} />
                    </ListItemButton>
                    <ListItemButton onClick={() => route(`/${role}/requests`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><DraftsIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="My Requests" {...listItemTextProps} />
                    </ListItemButton>
                    <ListItemButton onClick={() => route(`/${role}/cart`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25, display: { xs: 'flex', md: 'none' } }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><ShoppingCartIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="Cart" {...listItemTextProps} />
                    </ListItemButton>
                </>)}
                {(role === "Super" || role === "Admin") && (<>
                    <Divider sx={{ my: 1.5 }} />
                    <ListItemButton onClick={() => route(`/Admin/whatsappTemplates`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><WhatsApp sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="WhatsApp Templates" {...listItemTextProps} />
                    </ListItemButton>
                    <ListItemButton onClick={() => route(`/Admin/whatsappCampaigns`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><CampaignIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="WhatsApp Campaigns" {...listItemTextProps} />
                    </ListItemButton>
                    <ListItemButton onClick={() => route(`/Admin/whatsappConversations`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><ForumIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="WhatsApp Conversations" {...listItemTextProps} />
                    </ListItemButton>
                    <Divider sx={{ my: 1.5 }} />
                    <ListItemButton onClick={() => route(`/Admins/TicketRequests`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><DraftsIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="View Ticket Requests" {...listItemTextProps} />
                    </ListItemButton>
                    <ListItemButton onClick={() => route(`/events/create`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><AddCircleOutlineIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="Create Event" {...listItemTextProps} />
                    </ListItemButton>
                </>)}
                {(role === "Super") && (<>
                    <ListItemButton onClick={() => route(`/Super/create`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><StarsIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="Create Super Admin" {...listItemTextProps} />
                    </ListItemButton>
                    <ListItemButton onClick={() => route(`/Super/Admin/create`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><AdminPanelSettingsIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="Create Admin" {...listItemTextProps} />
                    </ListItemButton>
                    <ListItemButton onClick={() => route(`/Super/Partner/create`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><SupervisedUserCircleIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="Create Partner Account" {...listItemTextProps} />
                    </ListItemButton>
                </>)}
                {role === "User" && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <ListItemButton onClick={() => route(`/User/profile`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 42 }}><AccountCircle sx={navIconSx} /></ListItemIcon>
                            <ListItemText primary="My Profile" {...listItemTextProps} />
                        </ListItemButton>
                    </>
                )}
                {role !== "Usher" && (<>
                    <Divider sx={{ my: 1.5 }} />
                    <ListItemButton onClick={() => route(`/about`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><InfoIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="About Us" {...listItemTextProps} />
                    </ListItemButton>
                    <ListItemButton onClick={() => route(`/contact`)} sx={{ borderRadius: 1, mx: 1, mb: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 42 }}><ContactsIcon sx={navIconSx} /></ListItemIcon>
                        <ListItemText primary="Contact Us" {...listItemTextProps} />
                    </ListItemButton>
                </>)}
                {!platformNavConfig && renderDrawerFooter()}
            </List>
        </Box>
    );

    const navDrawer = (
        <Drawer
            anchor="left"
            open={sidebarOpen}
            onClose={closeSidebar}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
                sx: {
                    width: { xs: 'min(100vw - 40px, 320px)', sm: 320 },
                    maxWidth: '100vw',
                    boxSizing: 'border-box',
                    border: 'none',
                    borderRadius: 0,
                    boxShadow: '8px 0 32px rgba(30, 5, 80, 0.18)',
                },
            }}
        >
            {sidebarContent}
        </Drawer>
    );

    return (
        <Box sx={{ flexGrow: 1, color: "var(--primaryColor)" }}>
            <LoginModal open={loginOpen} handleClose={() => setLoginOpen(false)} />
            <>
                    <AppBar position="fixed" component="nav" sx={appBarSx}>
                        <Toolbar sx={{ minHeight: toolbarHeight }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                {/* Left Section - Logo and Menu */}
                                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                    {((!platformMode && location.pathname !== '/') ||
                                        (platformMode && getEventIdFromPath(location.pathname))) && (
                                        <IconButton
                                            size="large"
                                            edge="start"
                                            color="inherit"
                                            aria-label={platformMode ? 'Back to overview' : 'Go back'}
                                            sx={{
                                                mr: 1,
                                                color: 'white',
                                                display: { xs: 'inline-flex', md: platformMode ? 'none' : 'inline-flex' },
                                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                            }}
                                            onClick={() => platformMode ? handlePlatformHome() : navigate(-1)}
                                        >
                                            <ArrowBackIcon />
                                        </IconButton>
                                    )}
                                    {role && (
                                        <IconButton
                                            size="large"
                                            edge="start"
                                            color="inherit"
                                            aria-label="menu"
                                            sx={{
                                                mr: 2,
                                                color: 'white',
                                                display: { xs: 'none', md: platformMode ? 'none' : 'inline-flex' },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                }
                                            }}
                                            onClick={openSidebar}
                                        >
                                            <MenuIcon />
                                        </IconButton>
                                    )}
                                    <TickitLogo
                                        height={40}
                                        variant="onDark"
                                        onClick={platformMode ? handlePlatformHome : handleHome}
                                    />
                                </Box>

                                {/* Center Section - Navigation (marketing only) */}
                                {!platformMode && (
                                <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
                                    <Button
                                        variant="text"
                                        startIcon={<EventIcon />}
                                        onClick={() => route(role === "Partner" ? "/Partner/events" : "/events")}
                                        sx={{
                                            color: 'white',
                                            fontFamily: "'Syne', TickitOne, Helvetica, Arial, sans-serif",
                                            fontWeight: 700,
                                            textTransform: 'none',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                            }
                                        }}
                                    >
                                        Events
                                    </Button>

                                    {(!role || role === "User" || role === "Partner") && (
                                        <>
                                            <Button
                                                variant="text"
                                                startIcon={<ViewModuleIcon />}
                                                onClick={() => route("/features")}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: "'Syne', TickitOne, Helvetica, Arial, sans-serif",
                                                    fontWeight: 700,
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Features
                                            </Button>
                                            <Button
                                                variant="text"
                                                startIcon={<FavoriteIcon />}
                                                onClick={() => route("/events/request")}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: "'Syne', TickitOne, Helvetica, Arial, sans-serif",
                                                    fontWeight: 700,
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Weddings
                                            </Button>
                                        </>
                                    )}

                                    {(!role || role === "User") && (
                                        <>
                                            {role === "User" && (
                                                <Button
                                                    variant="text"
                                                    startIcon={<LocalActivityIcon />}
                                                    onClick={() => route(`/${role}/tickets`)}
                                                    sx={{
                                                        color: 'white',
                                                        fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                        fontWeight: 'bold',
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    Tickets
                                                </Button>
                                            )}
                                            <Button
                                                variant="text"
                                                startIcon={<InfoIcon />}
                                                onClick={() => route(`/about`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                About Us
                                            </Button>
                                            <Button
                                                variant="text"
                                                startIcon={<ContactsIcon />}
                                                onClick={() => route(`/contact`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Contact Us
                                            </Button>
                                        </>
                                    )}

                                    {(role === "Admin" || role === "Super") && (
                                        <>
                                            <Button
                                                variant="text"
                                                startIcon={<DraftsIcon />}
                                                onClick={() => route(`/Admins/TicketRequests`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Ticket Requests
                                            </Button>
                                            <Button
                                                variant="text"
                                                startIcon={<CampaignIcon />}
                                                onClick={() => route(`/Admin/whatsappCampaigns`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Campaigns
                                            </Button>
                                            <Button
                                                variant="text"
                                                startIcon={<ForumIcon />}
                                                onClick={() => route(`/Admin/whatsappConversations`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Chats
                                            </Button>
                                            <Button
                                                variant="text"
                                                startIcon={<SupervisedUserCircleIcon />}
                                                onClick={() => route(`/Super/Partner/search`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Partners
                                            </Button>
                                        </>
                                    )}

                                    {role === "Partner" && (
                                        <>
                                            <Button
                                                variant="text"
                                                startIcon={<BoltIcon />}
                                                onClick={() => route(`/events/request`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Host With Us
                                            </Button>
                                            <Button
                                                variant="text"
                                                startIcon={<InfoIcon />}
                                                onClick={() => route(`/about`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                About Us
                                            </Button>
                                            <Button
                                                variant="text"
                                                startIcon={<ContactsIcon />}
                                                onClick={() => route(`/contact`)}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                Contact Us
                                            </Button>
                                        </>
                                    )}
                                </Box>
                                )}

                                {/* Right Section - User Actions */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.25 }}>
                                    {platformMode && role && (
                                        <>
                                            {canToggleTheme && (
                                                <Tooltip title={themeMode === 'dark' ? 'Light mode' : 'Dark mode'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={toggleTheme}
                                                        aria-label="Toggle theme"
                                                        sx={{ color: 'rgba(255,255,255,0.45)', '&:hover': { color: '#fff' } }}
                                                    >
                                                        {themeMode === 'dark' ? (
                                                            <LightModeOutlinedIcon fontSize="small" />
                                                        ) : (
                                                            <DarkModeOutlinedIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.45)' }} aria-label="Notifications">
                                                <NotificationsNoneIcon fontSize="small" />
                                            </IconButton>
                                            <Tooltip title="Log out">
                                                <IconButton
                                                    size="small"
                                                    onClick={handleLogout}
                                                    aria-label="Log out"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.45)',
                                                        '&:hover': { color: '#fca5a5', bgcolor: 'rgba(239,68,68,0.12)' },
                                                    }}
                                                >
                                                    <LogoutIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton
                                                size="small"
                                                onClick={handleProfile}
                                                aria-label="Settings"
                                                sx={{ color: 'rgba(255,255,255,0.45)' }}
                                            >
                                                <SettingsOutlinedIcon fontSize="small" />
                                            </IconButton>
                                            <Box
                                                onClick={openAccountMenu}
                                                aria-label="Account menu"
                                                aria-haspopup="true"
                                                aria-expanded={accountMenuOpen ? 'true' : 'false'}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    bgcolor: platformColors.brand,
                                                    color: '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    cursor: 'pointer',
                                                    border: accountMenuOpen ? '2px solid rgba(255,255,255,0.5)' : '2px solid transparent',
                                                }}
                                            >
                                                {userInitials}
                                            </Box>
                                            <Menu
                                                anchorEl={accountMenuAnchor}
                                                open={accountMenuOpen}
                                                onClose={closeAccountMenu}
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                PaperProps={{
                                                    sx: {
                                                        mt: 1,
                                                        minWidth: 180,
                                                        borderRadius: '10px',
                                                        boxShadow: '0 8px 32px rgba(14,5,32,0.18)',
                                                    },
                                                }}
                                            >
                                                {role === "Partner" && (
                                                    <MenuItem onClick={handleProfile}>My profile</MenuItem>
                                                )}
                                                {(role === "Super" || role === "Admin" || role === "Partner") && (
                                                    <MenuItem onClick={handleChangePassword}>Change password</MenuItem>
                                                )}
                                                <Divider />
                                                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                                                    <ListItemIcon>
                                                        <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                                                    </ListItemIcon>
                                                    Log out
                                                </MenuItem>
                                            </Menu>
                                        </>
                                    )}
                                    {!platformMode && role === "User" && (
                                        <>
                                            <IconButton
                                                onClick={() => route(`/${role}/cart`)}
                                                sx={{
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                <ShoppingCartIcon />
                                            </IconButton>
                                            <Button
                                                variant="text"
                                                startIcon={<AccountCircle />}
                                                onClick={handleProfile}
                                                sx={{
                                                    color: 'white',
                                                    fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                    fontWeight: 'bold',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                    }
                                                }}
                                            >
                                                {auth.user.name}
                                            </Button>
                                        </>
                                    )}

                                    {!platformMode && role === "Partner" && (
                                        <Button
                                            variant="text"
                                            startIcon={<SupervisedUserCircleIcon />}
                                            onClick={handleProfile}
                                            sx={{
                                                color: 'white',
                                                fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                }
                                            }}
                                        >
                                            {auth.user.name}
                                        </Button>
                                    )}

                                    {!platformMode && (role === "Admin" || role === "Super") && (
                                        <IconButton
                                            onClick={() => route(`/${role}`)}
                                            sx={{
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                }
                                            }}
                                        >
                                            <HomeIcon />
                                        </IconButton>
                                    )}

                                    {!platformMode && !role && (
                                        <Button
                                            variant="contained"
                                            startIcon={<AccountCircle />}
                                            onClick={() => navigate("/Login")}
                                            sx={{
                                                backgroundColor: 'var(--primaryPurple)',
                                                color: 'white',
                                                fontFamily: 'TickitOne, Helvetica, Arial, sans-serif',
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    backgroundColor: 'var(--secPurple)'
                                                }
                                            }}
                                        >
                                            Login
                                        </Button>
                                    )}
                                    </Box>
                                    <IconButton
                                        size="large"
                                        edge="end"
                                        color="inherit"
                                        aria-label="menu"
                                        sx={{
                                            color: 'white',
                                            display: { xs: 'inline-flex', lg: 'none' },
                                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                                        }}
                                        onClick={openSidebar}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Toolbar>
                    </AppBar>
                    {navDrawer}
                </>
        </Box>
    );
}

const mapStateToProps = (state) => ({
    auth: state?.auth
});

const mapDispatchToProps = { logout };

export default connect(mapStateToProps, mapDispatchToProps)(MenuAppBar);